import { withScrollableArea } from '@approvalmax/ui';
import { arrayHelpers } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { useDispatch, useSelector, useShallowEqualSelector } from 'modules/react-redux';
import { getFieldsApprovalPermissionBySystemPurpose } from 'pages/requestForm/selectors/requestSelectors';
import { FC, memo, useCallback, useEffect } from 'react';

import {
    removeAttachment,
    xeroEmailToSupplierChangeData,
    xeroEmailToSupplierInit,
    xeroEmailToSupplierToggle,
} from '../../../actions';
import EmailToSupplierForm from '../../../components/EmailToSupplierForm/EmailToSupplierForm';
import {
    EmailToSupplierChangeValidator,
    EmailToSupplierContactPersonData,
    EmailToSupplierDataChangeHandler,
} from '../../../components/EmailToSupplierForm/EmailToSupplierForm.types';
import { XeroContact, XeroContactPerson } from '../../../data/xero/XeroContact';
import {
    getContext,
    getNewSupplierAttachments,
    getShowValidationErrors,
    isLoading,
} from '../../../selectors/pageSelectors';
import { getContacts } from './XeroEmailToSupplierSection.helpers';
import { XeroEmailToSupplierSectionProps } from './XeroEmailToSupplierSection.types';

const XeroEmailToSupplierSection: FC<XeroEmailToSupplierSectionProps> = memo((props) => {
    const { request, labels, placeholderList, isReadonly } = props;

    const dispatch = useDispatch();
    const details = request.details;
    const { sendToSupplier, emailToSupplier: data } = details;
    const supplier = details.contact as XeroContact | null;
    const attachments = details?.emailToSupplier?.attachments || [];
    const [emailToSupplierFieldPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.EmailToSupplier])
    );

    const { me, showErrors, newAttachments, loading } = useShallowEqualSelector((state: State) => {
        return {
            me: selectors.profile.getProfileUser(state),
            showErrors: getShowValidationErrors(state),
            newAttachments: getNewSupplierAttachments(state),
            loading: isLoading(state),
            context: getContext(state),
        };
    });

    useEffect(() => {
        dispatch(xeroEmailToSupplierInit(emailToSupplierFieldPermission.required && !isReadonly));
    }, [dispatch, emailToSupplierFieldPermission.required, isReadonly]);

    const changeData: EmailToSupplierDataChangeHandler = (value, key) => {
        if (data) {
            dispatch(xeroEmailToSupplierChangeData(value, key));
        }
    };

    const validateTo = useCallback<EmailToSupplierChangeValidator>((data) => {
        return data.to.length === 0;
    }, []);

    const filterContactPersonItem = useCallback((contact: EmailToSupplierContactPersonData, filterText: string) => {
        return (
            contact.text.toUpperCase().includes(filterText) || contact.emailAddress.toUpperCase().includes(filterText)
        );
    }, []);

    const onVisibilityToggle = useCallback(
        (sendToSupplier: boolean) => {
            dispatch(xeroEmailToSupplierToggle(sendToSupplier));
        },
        [dispatch]
    );

    const onAttachmentRemove = useCallback(
        (attachmentId: string) => {
            dispatch(removeAttachment(attachmentId));
        },
        [dispatch]
    );

    const contactPersons: XeroContactPerson[] =
        supplier && supplier.contactPersons ? supplier.contactPersons : arrayHelpers.emptyArray();

    return (
        <EmailToSupplierForm
            labels={labels}
            data={data}
            onChangeData={changeData}
            contacts={getContacts(contactPersons, me)}
            toOptionsFilterFunction={filterContactPersonItem}
            toValidatorFunction={validateTo}
            ccOptionsFilterFunction={filterContactPersonItem}
            attachments={attachments}
            attachmentsNew={newAttachments}
            isAttachmentsLoading={loading}
            onAttachRemove={onAttachmentRemove}
            isVisible={sendToSupplier}
            onVisibilityToggle={onVisibilityToggle}
            hasErrors={showErrors}
            integrationType={domain.IntegrationType.Xero}
            placeholderList={placeholderList}
            isReadonly={isReadonly || emailToSupplierFieldPermission.disabled}
            hideCheckbox={emailToSupplierFieldPermission.required}
        />
    );
});

const ScrollableXeroEmailToSupplierSection = withScrollableArea(XeroEmailToSupplierSection);

export default ScrollableXeroEmailToSupplierSection;
